import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { createCustomerRequest } from "../lib/api";
import {
  ChefHat,
  ShoppingCart,
  Truck,
  Layers,
  FileText,
  Bell,
  Package,
  ClipboardList,
  Check,
  ArrowRight,
  Loader2,
  Sparkles,
  Clock,
  Users2,
} from "lucide-react";

const FEATURES = [
  {
    icon: ShoppingCart,
    title: "Faster orders, fewer mistakes",
    body:
      "Build an order in minutes with smart sorting by vendor, category or name. See what you ordered last time and how much, so you never over- or under-order.",
  },
  {
    icon: Layers,
    title: "One dashboard for every location",
    body:
      "Run 1 restaurant or 15 — flip between them from a single dropdown. Combined mode lets you order for the whole group in a single flow.",
  },
  {
    icon: Truck,
    title: "Vendors stay organized",
    body:
      "Every item has a default vendor. Change a vendor once and it sticks. Combined mode aggregates vendors by name so nothing gets duplicated.",
  },
  {
    icon: FileText,
    title: "Order PDFs & one-tap share",
    body:
      "Every submitted order becomes a clean, vendor-grouped PDF. Share it to WhatsApp, email or SMS directly from your phone.",
  },
  {
    icon: Bell,
    title: "Real-time Telegram alerts",
    body:
      "Owner and managers get instant Telegram notifications when orders are submitted, finalized or received. No more chasing.",
  },
  {
    icon: ClipboardList,
    title: "Receive & reconcile",
    body:
      "When goods arrive, mark them received per vendor with invoice number and quantity. Partial deliveries handled cleanly.",
  },
];

const HOW_IT_WORKS = [
  { n: "01", title: "We set you up", body: "We migrate your item list, vendors and locations. You're live within a day." },
  { n: "02", title: "Managers place orders", body: "Staff opens the app on their phone, taps + / − on the items they need, and hits submit." },
  { n: "03", title: "Owner reviews & finalizes", body: "You get a Telegram ping, review the order, adjust anything and finalize with one tap." },
  { n: "04", title: "PDF goes to vendors", body: "Share the vendor-grouped PDF on WhatsApp. When goods arrive, mark them received in the app." },
];

const PROMISES = [
  "Works on any phone — Android or iPhone",
  "Installable as an app (no App Store)",
  "Unlimited items, vendors, orders",
  "Offline-friendly — the shell keeps loading",
  "PDF export & WhatsApp share built in",
  "Free 30-day trial — cancel anytime",
];

const initialForm = {
  restaurant_name: "",
  contact_name: "",
  email: "",
  phone: "",
  location_count: "1",
  current_tool: "",
  message: "",
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.restaurant_name.trim() || !form.contact_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in the required fields");
      return;
    }
    setIsSubmitting(true);
    try {
      await createCustomerRequest(form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Thanks! We'll be in touch within 24 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone text-stone-900 selection:bg-kale-200 selection:text-kale-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-bone/85 backdrop-blur-lg border-b border-stone-200/60">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-kale-900 flex items-center justify-center shadow-sm">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold tracking-tight text-lg text-kale-950">SupplierFlow</span>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-stone-700">
            <button onClick={() => scrollTo("features")} className="hover:text-kale-900 transition-colors" data-testid="nav-features">Features</button>
            <button onClick={() => scrollTo("how")} className="hover:text-kale-900 transition-colors" data-testid="nav-how">How it works</button>
            <button onClick={() => scrollTo("pricing")} className="hover:text-kale-900 transition-colors" data-testid="nav-pricing">Pricing</button>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-kale-900 hover:bg-kale-50" data-testid="nav-signin">
              Sign In
            </Button>
            <Button onClick={() => scrollTo("signup")} className="btn-primary hidden sm:inline-flex" data-testid="nav-cta">
              Start Free Trial
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-kale-200/40 blur-3xl" />
          <div className="absolute -top-24 right-0 w-[30rem] h-[30rem] rounded-full bg-amber-100/50 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-5 pt-16 pb-14 md:pt-24 md:pb-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-stone-200/70 text-xs font-medium text-kale-800">
              <Sparkles className="w-3.5 h-3.5" />
              Made for restaurants, not accountants
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-kale-950">
              Run your kitchen&apos;s supply
              <br className="hidden sm:block" />
              <span className="text-kale-700"> without the WhatsApp chaos.</span>
            </h1>
            <p className="text-lg text-stone-600 max-w-xl leading-relaxed">
              SupplierFlow turns messy vendor orders, screenshots and spreadsheets into one clean flow — for one location or fifteen. Your staff places orders in minutes; you approve from your phone.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={() => scrollTo("signup")} size="lg" className="btn-primary h-12 px-6 text-base" data-testid="hero-cta">
                Start 30-day free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => scrollTo("how")} size="lg" variant="outline" className="h-12 px-6 text-base border-kale-300 text-kale-900 hover:bg-kale-50" data-testid="hero-secondary">
                See how it works
              </Button>
            </div>
            <div className="flex items-center gap-5 pt-4 text-sm text-stone-500">
              <div className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Live in a day</div>
              <div className="hidden sm:flex items-center gap-1.5"><Users2 className="w-4 h-4" /> Unlimited users</div>
              <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-kale-700" /> No setup fee</div>
            </div>
          </div>

          {/* Illustrated card cluster */}
          <div className="md:col-span-5 relative">
            <div className="relative aspect-[4/5] max-w-sm mx-auto">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-kale-900 to-kale-700 shadow-2xl -rotate-3 rotate" />
              <div className="absolute inset-0 rounded-3xl bg-white shadow-xl rotate-2 flex flex-col p-5 gap-3">
                <div className="flex items-center justify-between">
                  <div className="w-9 h-9 rounded-xl bg-kale-100 flex items-center justify-center"><ChefHat className="w-5 h-5 text-kale-700"/></div>
                  <span className="text-xs font-mono px-2 py-0.5 bg-kale-50 text-kale-700 rounded-full">#240819-11-45</span>
                </div>
                <div className="text-sm font-semibold text-stone-800">Vegetables · Rana Farms</div>
                <div className="space-y-2 flex-1">
                  {[
                    { name: "Tomatoes", qty: "6 kg" },
                    { name: "Onions", qty: "10 kg" },
                    { name: "Coriander", qty: "2 bunch" },
                    { name: "Green chili", qty: "1 kg" },
                  ].map((r) => (
                    <div key={r.name} className="flex items-center justify-between p-2 rounded-lg bg-stone-50 border border-stone-100">
                      <span className="text-sm text-stone-700">{r.name}</span>
                      <span className="text-xs font-mono text-kale-800 bg-kale-50 px-1.5 py-0.5 rounded">{r.qty}</span>
                    </div>
                  ))}
                </div>
                <button className="w-full rounded-lg py-2 bg-clay-500 text-white text-sm font-semibold">Submit order</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-16 md:py-24 border-t border-stone-200/60">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-kale-700 mb-3">Features</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-kale-950">
              Everything a restaurant owner actually needs.
            </h2>
            <p className="text-stone-600 mt-3">
              No spreadsheets. No missed items. No 47-message-long WhatsApp threads.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {FEATURES.map((f) => (
              <Card key={f.title} className="border-stone-200/70 hover:border-kale-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-kale-100 flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5 text-kale-800" />
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{f.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-16 md:py-24 bg-kale-950 text-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-kale-300 mb-3">How it works</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              Four steps. That&apos;s the whole thing.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.n} className="relative p-6 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.07] transition-colors">
                <div className="text-4xl font-bold text-kale-300/70 mb-4 font-mono">{s.n}</div>
                <h3 className="font-semibold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5">
          <div className="text-center max-w-xl mx-auto">
            <p className="text-sm font-semibold uppercase tracking-wider text-kale-700 mb-3">Pricing</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-kale-950">
              One simple price. No surprises.
            </h2>
          </div>
          <div className="mt-12 rounded-3xl bg-gradient-to-br from-kale-900 to-kale-700 text-white p-8 md:p-10 shadow-2xl">
            <div className="grid md:grid-cols-5 gap-8 items-center">
              <div className="md:col-span-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-medium">
                  <Sparkles className="w-3.5 h-3.5" /> First 30 days free
                </div>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-6xl font-bold tracking-tight">$50</span>
                  <span className="text-white/80 text-lg">CAD / month</span>
                </div>
                <p className="text-white/70 mt-2">per restaurant. Cancel anytime.</p>
                <Button onClick={() => scrollTo("signup")} className="mt-6 h-12 px-6 bg-white text-kale-900 hover:bg-white/90 font-semibold" data-testid="pricing-cta">
                  Start free trial
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <div className="md:col-span-2 space-y-2.5">
                {PROMISES.map((p) => (
                  <div key={p} className="flex items-start gap-2.5 text-sm">
                    <div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3" />
                    </div>
                    <span className="text-white/90">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGN-UP FORM */}
      <section id="signup" className="py-16 md:py-24 bg-stone-50 border-t border-stone-200/60">
        <div className="max-w-3xl mx-auto px-5">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-kale-700 mb-3">Get started</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-kale-950">
              Tell us about your restaurant.
            </h2>
            <p className="text-stone-600 mt-3">
              We&apos;ll set you up within a day. No credit card required for the free trial.
            </p>
          </div>

          <Card className="border-stone-200/70 shadow-xl">
            <CardContent className="p-6 md:p-8">
              {submitted ? (
                <div className="py-8 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-kale-100 flex items-center justify-center mx-auto">
                    <Check className="w-8 h-8 text-kale-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-kale-950">Thanks! We&apos;ve got your request.</h3>
                  <p className="text-stone-600 max-w-md mx-auto">
                    Our team will reach out within 24 hours to set up your account and walk you through the app.
                  </p>
                  <div className="pt-2 flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => setSubmitted(false)} data-testid="signup-another">
                      Submit another
                    </Button>
                    <Link to="/login"><Button className="btn-primary" data-testid="signup-signin">Sign in</Button></Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="restaurant_name">Restaurant name *</Label>
                      <Input
                        id="restaurant_name"
                        value={form.restaurant_name}
                        onChange={(e) => update("restaurant_name", e.target.value)}
                        placeholder="The Virsa"
                        className="input-mobile"
                        required
                        data-testid="signup-restaurant-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Owner / contact name *</Label>
                      <Input
                        id="contact_name"
                        value={form.contact_name}
                        onChange={(e) => update("contact_name", e.target.value)}
                        placeholder="Beant Singh"
                        className="input-mobile"
                        required
                        data-testid="signup-contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@restaurant.com"
                        className="input-mobile"
                        required
                        data-testid="signup-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+1 587 000 0000"
                        className="input-mobile"
                        required
                        data-testid="signup-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Number of locations</Label>
                      <Select value={form.location_count} onValueChange={(v) => update("location_count", v)}>
                        <SelectTrigger className="input-mobile" data-testid="signup-locations">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 location</SelectItem>
                          <SelectItem value="2-5">2 – 5 locations</SelectItem>
                          <SelectItem value="6+">6+ locations</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="current_tool">Currently using</Label>
                      <Input
                        id="current_tool"
                        value={form.current_tool}
                        onChange={(e) => update("current_tool", e.target.value)}
                        placeholder="WhatsApp / paper / Excel …"
                        className="input-mobile"
                        data-testid="signup-current-tool"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Anything you&apos;d like us to know?</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="e.g. we run 3 kitchens and share a few suppliers…"
                      className="min-h-[90px] text-base"
                      data-testid="signup-message"
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full btn-primary h-12 text-base" data-testid="signup-submit">
                    {isSubmitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    ) : (
                      <>Request my free trial <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>
                  <p className="text-xs text-center text-stone-500">
                    By submitting you agree to be contacted about SupplierFlow. We&apos;ll never share your info.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-stone-200/60 bg-bone">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-kale-900 flex items-center justify-center">
              <ChefHat className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm text-stone-600">
              © {new Date().getFullYear()} SupplierFlow · Built by restaurant owners, for restaurant owners.
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/login" className="text-stone-600 hover:text-kale-900">Sign in</Link>
            <button onClick={() => scrollTo("signup")} className="text-kale-900 font-medium hover:underline">Start free trial</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
